/**
 * A STUDIO, IN GREYSCALE, FOR THINGS TO REFLECT WHEN THERE IS NOTHING THERE.
 *
 * Screen-space reflection can only reflect what is on the screen. A chrome
 * sphere facing the camera reflects the void behind it; a ray that leaves the
 * frame edge finds nothing; and the underwater probe that stands in for both is
 * a smooth blue gradient, which is the one thing a mirror cannot use. A mirror
 * needs STRUCTURE — a bright shape with an edge, so that turning the object
 * moves a highlight across it. Without one, chrome is just a grey ball.
 *
 * So: the oldest solution in product rendering, a softbox in a dark room. Three
 * sources and a floor, equirectangular, generated rather than shipped.
 *
 * DELIBERATELY MONOCHROME. It is a fallback, not a light — the scene's own IBL
 * carries the colour of the water and this only has to supply shape. Give it a
 * hue and every chrome object in the game starts wearing a studio that is not
 * in the world; leave it grey and it reads as specular structure, which is
 * exactly what is missing.
 *
 * Same 128 x 64 equirect and the same `atan2(x, z) / acos(y)` mapping as the
 * environment probe, so one sampler and one convention serve both. Mips are
 * box-filtered down the chain so a rough material gets a blurred studio for
 * free, which is what makes a satin finish read as satin rather than as a
 * mirror with a small light in it.
 */

/**
 * 512 x 256, NOT 128 x 64, AND THE REASON IS THE SOFTBOX'S EDGE.
 *
 * A mirror magnifies whatever it reflects, so the thing that has to survive is
 * not the map's average detail but the GRADIENT at the edge of the key light —
 * that is the whole feature. At 128 wide a texel is 2.8 degrees and the key's
 * falloff spans about six of them, which a chrome surface stretches across
 * half the object as a staircase.
 *
 * At 512 the same falloff is 23 texels and reads as a soft edge. It costs 1.3
 * MB with the full mip chain, against 0.08 — which for the difference between
 * chrome and a mosaic is not a trade worth thinking about.
 *
 * Ten mips rather than eight, so the chain still reaches 1 x 1 from the larger
 * base and a fully rough metal gets the average of the room rather than a
 * 4 x 2 patch of it.
 */
export const STUDIO_W = 512;
export const STUDIO_H = 256;
export const STUDIO_MIPS = 10;
/** Highest mip index, for the shader's roughness-to-lod mapping. */
export const STUDIO_MIP_MAX = STUDIO_MIPS - 1;

/** Half-float bits for a non-negative float. Enough for a light box. */
function half(v: number): number {
  if (!(v > 0)) return 0;
  if (v < 6.104e-5) return 0;
  const e = Math.min(30, Math.max(1, Math.floor(Math.log2(v)) + 15));
  const m = Math.round((v / Math.pow(2, e - 15) - 1) * 1024);
  return (e << 10) | Math.min(1023, Math.max(0, m));
}

/**
 * A soft rectangular source on the sphere.
 *
 * `az`/`el` in radians, `w`/`h` its angular half-size. Falloff is smoothstep on
 * each axis independently, which gives a box with soft edges rather than a
 * blob — and the EDGE is the whole point. A round gradient reflects as a round
 * gradient at every angle; a rectangle with corners tells you the object turned.
 */
function box(
  az: number, el: number, dirAz: number, dirEl: number,
  w: number, h: number, blur: number,
): number {
  let da = az - dirAz;
  while (da > Math.PI) da -= Math.PI * 2;
  while (da < -Math.PI) da += Math.PI * 2;
  const de = el - dirEl;
  // `blur` widens the transition to span several texels of the mip being built.
  // See `level`: without it a coarse mip is a SHARP field point-sampled, which
  // is aliasing rather than filtering.
  //
  // `min`, NOT `max`. The source has its own soft edge at 55 percent of its
  // half-size, and that is the LOOK; blur may only widen it further. With
  // `max` a fine mip — where blur is a fraction of a degree — got an inner
  // edge at `w - blur`, which is a HARDER edge than the one authored. Measured:
  // 44 percent step at mip 0 with `max`, 6 percent with `min`.
  const fx = 1 - smooth(Math.abs(da), Math.min(w * 0.55, w - blur), w + blur);
  const fy = 1 - smooth(Math.abs(de), Math.min(h * 0.55, h - blur), h + blur);
  return fx * fy;
}

function smooth(x: number, a: number, b: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / Math.max(b - a, 1e-6)));
  return t * t * (3 - 2 * t);
}

/**
 * One mip level, as raw rgba16f bytes — PREFILTERED, not point-sampled.
 *
 * This is the whole reason the reflections were blocky, and the previous
 * comment here was confidently wrong. It said the sources are analytic so each
 * mip could be evaluated at its own resolution rather than box-filtered down
 * the chain. Evaluating a SHARP field at low resolution is not filtering. It is
 * point sampling, and it aliases: at mip 4 the key light's edge fell inside one
 * texel, so a rough material magnified a 32-wide bitmap of hard squares.
 *
 * Being analytic is an advantage, but the way to use it is to widen the sources
 * as the mip coarsens. Every edge is then band-limited to the grid it is being
 * written onto, which is what a prefiltered environment map IS — and unlike a
 * box chain it is exact rather than an approximation of a blur.
 *
 * `mip = rough * 9` stays, and now means something: at roughness 0.42 the map
 * is 37 texels wide and genuinely soft, instead of 37 texels of staircase.
 */
function level(w: number, h: number): Uint8Array<ArrayBuffer> {
  // FOUR AND A HALF TEXELS OF THIS MIP, in radians. One is not enough: a
  // single-texel ramp still steps hard when the peak is 3.4, and bilinear
  // magnification turns a three-texel ramp into visible facets. Swept, as the
  // largest step between neighbouring texels over the whole map:
  //
  //     texels   mip 0   mip 2   mip 4   mip 6
  //        1.6      6%     20%     44%     65%
  //        3.0      6%     17%     24%     33%
  //        4.5      5%     15%     18%     21%
  //        6.0      5%     12%     15%     15%
  //
  // 4.5 flattens the coarse end without spreading the key so far that it loses
  // its peak — 6.0 costs another 17 percent of the highlight to buy three
  // points of smoothness nobody can see.
  const blur = (Math.PI * 2) / w * 4.5;
  const buf = new ArrayBuffer(w * h * 4 * 2);
  const out = new Uint16Array(buf);
  for (let y = 0; y < h; y++) {
    // v maps to acos(dir.y), so el runs +PI/2 at the top to -PI/2 at the bottom.
    const el = Math.PI * 0.5 - ((y + 0.5) / h) * Math.PI;
    for (let x = 0; x < w; x++) {
      const az = (((x + 0.5) / w) - 0.5) * Math.PI * 2;

      // THE ROOM. Dark, but not black: a mirror in a void has no silhouette at
      // all, and the point of this is that something is always there.
      let v = 0.030;
      // Floor a touch lighter than the walls and shading off downward, which is
      // what puts a horizon in the reflection. A horizon is the single strongest
      // cue that a reflective object is sitting in a place.
      v += 0.055 * smooth(-el, 0.0, Math.max(0.55, blur * 2));
      v -= 0.020 * smooth(-el, 0.5, 1.4);

      // KEY: the big softbox, high and to one side. 3.4 so it is genuinely
      // over-range — a highlight that clips is a highlight you can see roll.
      v += 3.40 * box(az, el, -0.85, 0.72, 0.62, 0.46, blur);
      // FILL: broad, opposite, and weak. It stops the dark side of a sphere
      // going to nothing without competing with the key for the highlight.
      v += 0.42 * box(az, el, 1.95, 0.16, 1.25, 0.95, blur);
      // RIM: a narrow strip low and behind, which is what draws the edge of a
      // dark object against a dark background.
      v += 1.15 * box(az, el, 3.02, -0.28, 0.30, 0.20, blur);

      const o = (y * w + x) * 4;
      const g = half(v);
      out[o] = g; out[o + 1] = g; out[o + 2] = g; out[o + 3] = half(1);
    }
  }
  return new Uint8Array(buf);
}

export function buildStudioTexture(device: GPUDevice): GPUTexture {
  const tex = device.createTexture({
    size: [STUDIO_W, STUDIO_H],
    format: 'rgba16float',
    mipLevelCount: STUDIO_MIPS,
    // COPY_SRC so the map can be read back and checked. It is generated code
    // producing a texture nobody can see directly — the only way to know a mip
    // is band-limited rather than aliased is to fetch it and measure the step
    // between neighbouring texels. Costs a usage flag.
    usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST
      | GPUTextureUsage.COPY_SRC,
    label: 'studio',
  });
  // Each mip generated at its own resolution AND widened to match it. See
  // `level` — evaluating the sharp field at 8 x 4 was the bug, not the plan.
  for (let m = 0; m < STUDIO_MIPS; m++) {
    const w = Math.max(1, STUDIO_W >> m), h = Math.max(1, STUDIO_H >> m);
    device.queue.writeTexture(
      { texture: tex, mipLevel: m },
      level(w, h),
      { bytesPerRow: w * 8, rowsPerImage: h },
      { width: w, height: h },
    );
  }
  return tex;
}
